import {
  usePrototypeFileSubscription,
  PrototypeFileFragment,
  useGetPrototypeFileForDocumentQuery,
} from '@sketch/gql-types'
import { RenderStatus } from 'utils/renderStatus'
import { useState } from 'react'
import { useVersioning } from 'modules/versioning'

type UsePrototypeStatusSubscriptionProps = {
  documentRenderStatus: RenderStatus | null
  prototypeFile?: PrototypeFileFragment['prototypeFile']
  documentIdentifier?: string
}

/**
 * Subscribe to the prototype file status change if needed (i.e. if the file
 * status is PENDING). When we finally get a valid download url for the file
 * then update the Apollo cache.
 */
export function usePrototypeStatusSubscription({
  documentRenderStatus,
  prototypeFile,
  documentIdentifier,
}: UsePrototypeStatusSubscriptionProps) {
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false)
  const versioning = useVersioning()

  const shouldSubscribe =
    typeof documentIdentifier === 'string' &&
    documentRenderStatus === 'ready' &&
    prototypeFile?.status === 'PENDING'

  usePrototypeFileSubscription({
    skip: !shouldSubscribe,
    variables: { documentId: documentIdentifier ?? '' },
    onSubscriptionData() {
      setSubscriptionSuccess(true)
    },
  })

  // The subscription response does not contain all the information we need, so
  // we just repeat the query for the prototypeFile and let Apollo update the
  // cache for us. This should insert all the properties into the `presentationFile`
  // object we need to unblock the loading flow in the parent PrototypeView
  // component.
  useGetPrototypeFileForDocumentQuery({
    variables: {
      identifier: versioning.share.identifier,
      versionShortId: versioning.versionShortId,
    },
    skip: subscriptionSuccess === false,
    fetchPolicy: 'network-only',
  })
}
